import { FC, type JSX } from 'react';
import Head from 'next/head';
import { css } from 'styled-components';
import { Center, Page } from 'src/components';
import Notification from 'src/apps/Tools/Notification';
import { LearningSessionError, WebSessionType } from 'src/api/learningSessions/types';
import { getSessionTypeVars } from '../../sessionConfig';
import { LearningSessionHeader } from './LearningSessionHeader';
import { BaseSessionErrorModal } from './BaseSessionErrorModal';
import * as S from './styles';

const flushPageBackground = css`
  background-color: #000;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 100%;
`;

type BaseSessionProps = {
  children?: JSX.Element;
  title: string;
  error: LearningSessionError | null;
  loading: boolean;
  sessionType: WebSessionType;
  sendCurrentLearningProgress: () => Promise<void>;
  onReportIssueClick?: () => void;
  analyticsSessionId?: string;
  showLearningToolsCoachmark?: boolean;
  onDismissLearningToolsCoachmark?: () => void;
  flushLayout?: boolean;
  /** Hide the classic colored session header (e.g. while blob layout is pending). */
  hideHeader?: boolean;
};

export const BaseSession: FC<BaseSessionProps> = ({
  children,
  loading,
  title,
  error,
  sessionType,
  sendCurrentLearningProgress,
  onReportIssueClick,
  analyticsSessionId,
  showLearningToolsCoachmark,
  onDismissLearningToolsCoachmark,
  flushLayout = false,
  hideHeader = false,
}) => {
  const { width, ...headerStyles } = getSessionTypeVars(sessionType);

  return (
    <Page footerStyle="none" contentBackground={flushLayout ? flushPageBackground : undefined}>
      <Notification />
      <S.Root data-testid={`${sessionType}-testId`} $transparent={flushLayout}>
        <Head>
          <title>{title} - Memrise</title>
        </Head>
        {!hideHeader && (
          <LearningSessionHeader
            title={title}
            sessionType={sessionType}
            sendCurrentLearningProgress={sendCurrentLearningProgress}
            onReportIssueClick={onReportIssueClick}
            analyticsSessionId={analyticsSessionId}
            showLearningToolsCoachmark={showLearningToolsCoachmark}
            onDismissLearningToolsCoachmark={onDismissLearningToolsCoachmark}
            {...headerStyles}
          />
        )}
        <S.Content $flush={flushLayout}>
          <>
            {error ? (
              <BaseSessionErrorModal sessionType={sessionType} error={error} />
            ) : loading ? (
              <Center $centerAlignItems $maxWidth={width} $fullWidth={flushLayout}>
                <S.Loader data-testid="loader" />
              </Center>
            ) : (
              <Center $maxWidth={width} $fullWidth={flushLayout}>
                {children}
              </Center>
            )}
          </>
        </S.Content>
      </S.Root>
    </Page>
  );
};
